import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { FormGroup, FormControlLabel, FormHelperText, Checkbox, TextField, IconButton, Button, Box, Link } from '@mui/material';
import Grid from '@mui/material/Grid2'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoIcon from '@mui/icons-material/Info';

import { CustomModal } from '../../Shared/CustomModal';
import { ImageThumbnail } from "../../../shared-components";
import AddStockModal from "../addStockModal";
import OnDemandArtworkModal from './OnDemandArtworkModal';

const Variant = (props) => {
  const [variant, setVariant] = useState({ ...props.object })
  const [modalState, setModalState] = useState(false);
  const [selectedImage, setSelectedImage] = useState(undefined);
  const [selectedDocument, setSelectedDocument] = useState({});
  const inputFileField = useRef(null);
  const documentFileField = useRef(null);
  const [addStock, setAddStock] = useState(false)

  const ON_DEMAND_PLACEMENTS = [ 'front', 'frontLeft', 'frontRight', 'back', 'backLeft', 'backRight', 'backTop', 'leftArm', 'rightArm', ];

  const [openOnDemandArtwork, setOpenOnDemandArtwork] = useState(false);
  const cdnBaseUrl = global?.constants?.cdnBaseUrl || '';

  useEffect(() => {
    setVariant(props.object)
  }, [props.object])

  const updateVariantState = (event) => {
    const field = event.target.name
    let _variant = { ...variant }

    if (event.target.type === 'checkbox') {
      _variant[field] = !!event.target.checked
    } else if (event.target.type === 'file') {
      const file = event.target.files?.[0];
      if (!file) return;

      _variant[field] = file

      if (field === 'image_path') {
        setSelectedImage(URL.createObjectURL(file));
        setModalState(false)
      } else {
        setSelectedDocument({ name: file.name, url: URL.createObjectURL(file) });
      }
    } else {
      _variant[field] = event.target.value
    }

    props.onUpdate(_variant)
    setVariant({ ..._variant })
  }

  const setExistingImage = (image) => {
    setSelectedImage(image.image)
    let _variant = { ...variant };
    _variant['image_path'] = `${image.image}?existing=true`;
    props.onUpdate(_variant);
    setModalState(false);
    setVariant({ ..._variant })
  }

  const removeVariant = () => {
    const _variant = { ...variant }
    _variant._delete = true
    props.onUpdate(_variant)
  }

  const uploadNewImage = () => {
    inputFileField.current?.click();
  }

  const uploadNewDocument = () => {
    documentFileField.current?.click();
  }

  const renderVariantImagePreview = () => {
    if (selectedImage) {
      return <ImageThumbnail image={selectedImage} height='55px' width='55px' />
    } else if (variant?.image_path) {
      return <ImageThumbnail image={variant?.image_path} height='55px' width='55px' />
    } else {
      return <AddPhotoAlternateIcon />
    }
  }

  const renderVariantDocumentPreview = () => {
    if (selectedDocument?.url) {
      return <a href={selectedDocument.url} target='_blank' rel="noreferrer">{selectedDocument.name}</a>
    } else if (variant?.document_path) {
      return <a href={variant.document_path} target='_blank' rel="noreferrer">{global.constants.labels.proof_sheet}</a>
    }
    return null;
  }

  const renderVariantModalBody = () => {
    return (
      <Box sx={{ padding: 2 }}>
        <div className="gallery">
          <input
            name='image_path'
            type='file'
            id='file'
            ref={inputFileField}
            onChange={updateVariantState}
            style={{ display: 'none' }}
          />
          <div className='new-image-container' onClick={uploadNewImage}>
            <button type="button" className='new-image-button'>{global.constants.labels.add}</button>
          </div>

          {props?.optionalParams['images']?.map((image, index) =>
            <div key={index} className='new-image-container' onClick={() => setExistingImage(image)}>
              <img src={image.image} alt="" />
            </div>
          )}
        </div>
      </Box>
    )
  }

  const handleVariantQuantityAdded = (quantity, incoming_inventory) => {
    if (!incoming_inventory) {
      let _variant = { ...variant, quantity: parseInt(variant.quantity) + parseInt(quantity) }
      setVariant(_variant)
      props.onUpdate(_variant)
    }
  }

  const getOnDemandPlacementSummary = () => {
    const existing = variant?.on_demand_artworks || {};
    const existingCount = Object.keys(existing).length;

    const removeList = Array.isArray(variant?.on_demand_artworks_remove)
      ? variant.on_demand_artworks_remove
      : [];
    const removedCount = removeList.length;

    const pendingCount = ON_DEMAND_PLACEMENTS.filter((p) => {
      const f = variant?.[`artwork_${p}`];
      return f instanceof File;
    }).length;

    const totalAfterSave = Math.max(existingCount + pendingCount - removedCount, 0);

    return { existingCount, pendingCount, removedCount, totalAfterSave };
  };

  const applyOnDemandArtwork = ({ draft, removePlacements }) => {
    const _variant = { ...variant };

    Object.entries(draft || {}).forEach(([placement, file]) => {
      _variant[`artwork_${placement}`] = file;
    });

    if (removePlacements?.length) {
      _variant.on_demand_artworks_remove = removePlacements;
    } else {
      delete _variant.on_demand_artworks_remove;
    }
    if (_variant.on_demand_artworks && removePlacements?.length) {
      const next = { ..._variant.on_demand_artworks };
      removePlacements.forEach(p => delete next[p]);
      _variant.on_demand_artworks = next;
    }

    props.onUpdate(_variant);
    setVariant(_variant);
    setOpenOnDemandArtwork(false);
  };

  const { pendingCount, removedCount, totalAfterSave } = getOnDemandPlacementSummary();

  return (
    <FormGroup
      style={{
        border: '1px solid #f1f1f1',
        borderRadius: '5px',
        padding: '10px',
        boxShadow: '0px 3px 5px 0px #f1f1f1',
        marginTop: '10px'
      }}
      className={(variant._delete ? 'hidden' : '')}
    >
      <CustomModal
        modalState={modalState}
        title={`${global.constants.labels.select} ${global.constants.labels.image}`}
        handleModalState={setModalState}
        modalBody={renderVariantModalBody()}
      />

      {addStock &&
        <AddStockModal
          productVariant={variant}
          variantId={variant.id}
          handleVariantQuantityAdded={handleVariantQuantityAdded}
          closeModal={() => setAddStock(false)}
        />
      }

      <Grid container spacing={2}>
        <Grid size={{ xs: 2 }}>
          <div
            onClick={() => setModalState(true)}
            className={(!selectedImage && !variant?.image_path) ? 'image-preview' : 'image-display-preview'}
          >
            {renderVariantImagePreview()}
          </div>
        </Grid>

        <Grid size={{ xs: 6 }}>
          <TextField
            type="text"
            label="Name"
            name="name"
            fullWidth
            variant="outlined"
            disabled={true}
            value={variant.name}
            onChange={updateVariantState}
          />
        </Grid>

        <Grid size={{ xs: 2 }}>
          <TextField
            type="number"
            min={0}
            label="Price"
            name="price"
            variant="outlined"
            value={variant.price}
            onWheel={(e) => e.target.blur()}
            onChange={updateVariantState}
          />
        </Grid>

        <Grid size={{ xs: 1 }}>
          <IconButton
            color="error"
            size="medium"
            onClick={() => { if (window.confirm(global.constants.labels.are_you_sure)) removeVariant() }}
          >
            <DeleteIcon />
          </IconButton>
        </Grid>

        <Grid size={{ xs: 1 }}>
          <IconButton
            {...props.attributes}
            {...props.listeners}
            color="primary"
            size="medium"
          >
            <DragIndicatorIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ paddingTop: 0.8 }}>
        <Grid size={{ xs: 3 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  name="manage_stock"
                  color="primary"
                  checked={variant.manage_stock === true}
                  onChange={updateVariantState}
                />
              }
              label="Track Inventory"
            />
          </FormGroup>
        </Grid>

        <Grid size={{ xs: 9 }} sx={{ marginTop: 0.5 }}>
          {!props.optionalParams?.isOnDemand ? (
            <>
              <input
                name="document_path"
                type="file"
                id="document"
                ref={documentFileField}
                onChange={updateVariantState}
                style={{ display: 'none' }}
              />
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                color="primary"
                size="small"
                onClick={uploadNewDocument}
              >
                <UploadFileIcon /> {global?.constants?.labels?.artwork || 'Artwork'}
              </Button>
              <br />
              {renderVariantDocumentPreview()}
            </>
          ) : (
            <>
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                size="small"
                onClick={() => setOpenOnDemandArtwork(true)}
              >
                <UploadFileIcon /> Upload On-Demand Artwork
              </Button>

              <FormHelperText>
                {totalAfterSave === 0 ? 'No placements selected' : `${totalAfterSave} placements selected`}
                {pendingCount ? ` (${pendingCount} pending upload)` : ''}
                {removedCount ? ` (${removedCount} will delete)` : ''}
              </FormHelperText>
            </>
          )}
        </Grid>
      </Grid>

      {!variant.manage_stock ? null : (
        <React.Fragment>
          <Grid container spacing={2} sx={{ paddingTop: 2 }} alignItems='center'>
            <Grid size={{ xs: 3 }}>
              <TextField
                id="variantQuantity"
                type="number"
                min={0}
                label="Current Stock"
                name="quantity"
                variant="outlined"
                inputProps={{ readOnly: !props?.optionalParams['isVariantOptionsEditing'] }}
                value={variant.quantity || 0}
                onChange={updateVariantState}
              />
            </Grid>

            {!props?.optionalParams['isVariantOptionsEditing'] &&
              <Grid size={{ xs: 2 }}>
                <Link>
                  <div style={{ cursor: 'pointer' }} onClick={() => setAddStock(true)}>Add Stock</div>
                </Link>
              </Grid>
            }

            <Grid size={{ xs: 7 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="allow_backorders"
                    color="primary"
                    checked={variant.allow_backorders === true}
                    onChange={updateVariantState}
                  />
                }
                label="Continue selling when out of stock"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ paddingTop: 2 }}>
            <Grid size={{ xs: 3 }}>
              <TextField
                type="text"
                label="SKU"
                name="sku"
                variant="outlined"
                value={variant.sku}
                onChange={updateVariantState}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              {props?.optionalParams['isBundle'] && (
                <TextField
                  type="number"
                  label="SKU Multiple"
                  name="sku_multiple"
                  variant="outlined"
                  onWheel={(e) => e.target.blur()}
                  value={variant.sku_multiple}
                  onChange={updateVariantState}
                />
              )}
            </Grid>

            <Grid size={{ xs: 6 }}>
              {props?.optionalParams['isBundle'] && (
                <Grid container spacing={2} sx={{ padding: 1 }}>
                  <Grid size={{ xs: 1 }}><InfoIcon /></Grid>
                  <Grid size={{ xs: 11 }}><FormHelperText>{global.constants.labels.sku_multiple_help}</FormHelperText></Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </React.Fragment>
      )}

      {props.optionalParams?.isOnDemand && (
        <OnDemandArtworkModal
          open={openOnDemandArtwork}
          onClose={() => setOpenOnDemandArtwork(false)}
          variantKey={variant.id || variant._id}
          placements={ON_DEMAND_PLACEMENTS}
          existingArtworks={variant?.on_demand_artworks || {}}
          cdnBaseUrl={cdnBaseUrl}
          initialDraft={ON_DEMAND_PLACEMENTS.reduce((acc, p) => {
            const f = variant?.[`artwork_${p}`];
            if (f instanceof File) acc[p] = f;
            return acc;
          }, {})}
          initialRemovePlacements={Array.isArray(variant?.on_demand_artworks_remove) ? variant.on_demand_artworks_remove : []}
          onApply={applyOnDemandArtwork}
        />
      )}
    </FormGroup>
  )
}

Variant.proptypes = {
  object: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default Variant